import React, { FC } from "react";
import {
  Modal,
  Button,
  Upload,
  Input,
  Typography,
  Card,
  Space,
  Tag
} from "antd";
import { UploadOutlined, EyeOutlined, FileTextOutlined, MessageOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { HMO_ROLE } from "../../../../utils/hmoRoles";
import QueryResponseDocumentsUpload, {
  buildDeficiencyDocumentRow,
  type QueryDocumentUploadRow,
} from "../QueryResponseDocumentsUpload";
import type { QueryUploadDocument } from "../../../../utils/queryDocumentUtils";
import "../../patient-list/PatientListModal.css";

const { TextArea } = Input;
const { Text } = Typography;

type DeficiencyUploadModalProps = {
  visible: boolean;
  onClose: () => void;
  replyQuery: any;
  handleOpen: any;
  documentUrls: QueryUploadDocument[];
  onConfirmUpload?: (
    row: QueryDocumentUploadRow,
    file: File,
  ) => Promise<boolean>;
  handleFileChange?: any;
  isReplying: boolean;
  handleSubmit: any;
  setResponseRemark: CallableFunction;
  responseRemark: any;
  handleViewDeficiencyFailedDocument: any;
};

const DeficiencyUploadModal: FC<DeficiencyUploadModalProps> = ({
  visible,
  onClose,
  replyQuery,
  handleOpen,
  documentUrls,
  onConfirmUpload,
  handleFileChange,
  isReplying,
  handleSubmit,
  setResponseRemark,
  responseRemark,
  handleViewDeficiencyFailedDocument,
}) => {
  const { auth } = useSelector((state: RootState) => state);
  return (
    <>
    <Modal
      title={
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 0"
        }}>
          <Space>
            <FileTextOutlined style={{ color: "#00549e", fontSize: 18 }} />
            <span style={{ fontSize: 16, fontWeight: 600, color: "#00549e" }}>
              DEFICIENCY QUERY RECEIVED
            </span>
          </Space>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1100}
      className="create-query-modal"
      style={{
        borderRadius: 12,
        overflow: "auto",
        top: 2,
          left: 4,
          padding: 0,
      }}
      bodyStyle={{
        padding: "24px",
        background: "#fafafa"
      }}
    >
      {/* Query Details Section */}
      <Card
        style={{
          marginBottom: 24,
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          border: "1px solid #e5e7eb"
        }}
        bodyStyle={{ padding: "20px" }}
      >
        <div style={{ marginBottom: 16 }}>
          <Space align="center" style={{ marginBottom: 12 }}>
            <MessageOutlined style={{ color: "#ef4444", fontSize: 16 }} />
            <Text strong style={{ fontSize: 14, color: "#374151" }}>
              QUERY DETAILS
            </Text>
          </Space>
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 6,
              padding: 16,
              color: "#dc2626",
              fontSize: 14,
              lineHeight: 1.5
            }}
          >
            {replyQuery?.receivedQuery}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text style={{ fontSize: 13, color: "#6b7280" }}>Type of Query:</Text>
          <Tag color="blue" style={{ fontSize: 12, padding: "2px 8px" }}>
            {replyQuery?.typeOfQuery}
          </Tag>
        </div>
      </Card>

      {/* Response Section */}
      {auth?.role?.role === HMO_ROLE.HSP_USER && (
        <Card
          style={{
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            border: "1px solid #e5e7eb"
          }}
          bodyStyle={{ padding: "20px" }}
        >
          <Space align="center" style={{ marginBottom: 20 }}>
            <MessageOutlined style={{ color: "#00549e", fontSize: 16 }} />
            <Text strong style={{ fontSize: 14, color: "#374151" }}>
              RESPONSE
            </Text>
          </Space>

          <div style={{ marginBottom: 20 }}>
            <QueryResponseDocumentsUpload
              rows={buildDeficiencyDocumentRow(replyQuery?.oldDocument)}
              uploads={documentUrls}
              onConfirmUpload={onConfirmUpload}
              onUpload={
                onConfirmUpload
                  ? undefined
                  : (row, file) =>
                      handleFileChange?.(row.documentType, row.label)({ file })
              }
            />
            <div style={{ marginTop: 8 }}>
              <Button
                style={{
                  borderColor: "#dc2626",
                  color: "#dc2626",
                  borderRadius: 6,
                }}
                onClick={() => handleViewDeficiencyFailedDocument?.(replyQuery)}
              >
                View Deficiency
              </Button>
            </div>
          </div>

          <div>
            <Text strong style={{ fontSize: 13, color: "#374151", marginBottom: 8, display: "block" }}>
              Response Remarks
            </Text>
            <TextArea
              value={responseRemark}
              onChange={(e) => setResponseRemark(e.target.value)}
              rows={4}
              placeholder="Enter your response remarks here..."
              style={{
                borderRadius: 6,
                border: "1px solid #d1d5db",
                resize: "none"
              }}
            />
          </div>
        </Card>
      )}

      {/* Action Buttons */}
      {auth?.role?.role === HMO_ROLE.HSP_USER && (
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: 16,
          marginTop: 20,
          paddingTop: 16,
          borderTop: "1px solid #e5e7eb"
        }}>
          <Button
            onClick={onClose}
            size="large"
            style={{
              borderColor: "#d1d5db",
              color: "#6b7280",
              borderRadius: 8,
              fontWeight: 500,
              padding: "8px 24px"
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isReplying}
            size="large"
            style={{
              backgroundColor: "#00549e",
              borderColor: "#00549e",
              borderRadius: 8,
              fontWeight: 500,
              padding: "8px 24px",
              boxShadow: "0 2px 4px rgba(0,84,158,0.2)"
            }}
          >
            Submit Response
          </Button>
        </div>
      )}
    </Modal>
    </>
  );
};

export default DeficiencyUploadModal;
